import type { ListObrasQuery } from '@obras/shared';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { HardHat, LayoutGrid, List as ListIcon, Plus, SlidersHorizontal } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { PageHeader } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { EmptyState, ErrorState, RefreshingBar } from '../components/ui/Feedback.js';
import { Pagination } from '../components/ui/Pagination.js';
import { Segmented, type SegmentedOption } from '../components/ui/Segmented.js';
import { ObraCard, ObraCardSkeleton, obraGrid, obraGridItem } from '../features/obras/ObraCard.js';
import { ObrasLista, ObrasListaSkeleton } from '../features/obras/ObrasLista.js';
import {
  INITIAL_FILTERS,
  NO_FILTERS,
  ObraFilterBar,
  SummaryStrip,
  countActiveFilters,
  restoreFilters,
  useObraQueryFilters,
  type ObraFilterState,
} from '../features/obras/Recorte.js';
import { useObras } from '../features/obras/api.js';
import { hasObrasSearch, type ObrasSearch } from '../features/obras/search.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';
import { useStoredState } from '../lib/storage.js';

const ICON = { strokeWidth: 1.5 } as const;

/** O recorte da listagem: o do acervo e a página. */
interface ListState extends ObraFilterState {
  readonly page: number;
}

const INITIAL_LIST_STATE: ListState = { ...INITIAL_FILTERS, page: 1 };

/**
 * O recorte que veio pela URL vira o estado inteiro da lista, não um acréscimo.
 *
 * Quem clica em "contratos vencidos" no painel quer ver exatamente isso; somar
 * ao que estava guardado — um fiscal, uma busca — devolveria uma lista que não
 * corresponde ao número clicado.
 */
function listStateFromSearch(search: ObrasSearch): ListState {
  const csv = (value: string | undefined): string[] => (value ? value.split(',') : []);

  return restoreListState({
    ...INITIAL_LIST_STATE,
    pendencias: csv(search.pendencias),
    statuses: csv(search.statuses),
    fiscalIds: csv(search.fiscalIds),
  }) as ListState;
}

/** O recorte guardado no navegador, mais a página em que a pessoa estava. */
function restoreListState(raw: unknown): ListState | undefined {
  const filters = restoreFilters(raw);
  if (!filters) return undefined;

  const { page } = raw as { page?: unknown };
  return {
    ...filters,
    page: typeof page === 'number' && Number.isInteger(page) && page >= 1 ? page : 1,
  };
}

/** Os dois modos da listagem: a grade de cartões e a lista em linhas. */
type Visao = 'cartoes' | 'lista';

const VISOES: readonly SegmentedOption<Visao>[] = [
  { value: 'cartoes', label: 'Cartões', icon: LayoutGrid },
  { value: 'lista', label: 'Lista', icon: ListIcon },
];

export function ObrasPage() {
  const { can, may, user, organization } = useAuth();

  /*
   * O recorte fica guardado no navegador, por pessoa e órgão: quem sai para
   * abrir uma obra e volta encontra os mesmos filtros e a mesma página. É a
   * tela em que mais se entra e sai, e refazer o recorte a cada volta era o
   * que mais custava.
   */
  const urlSearch = useSearch({ from: '/protected/obras' });
  const navigate = useNavigate();

  /*
   * Recorte vindo pela URL (do painel) tem precedência sobre o guardado, e é
   * lido uma vez, na montagem: depois de aplicado, a URL é limpa, para que
   * recarregar a tela ou voltar da obra não o reimponha sobre o que a pessoa
   * mudou desde então.
   */
  const [fromUrl] = useState(() =>
    hasObrasSearch(urlSearch) ? listStateFromSearch(urlSearch) : null,
  );
  const [listState, setListState] = useStoredState(
    `obras.lista:${organization?.id ?? '-'}:${user?.id ?? '-'}`,
    fromUrl ?? INITIAL_LIST_STATE,
    (raw) => fromUrl ?? restoreListState(raw),
  );
  const { search, page } = listState;

  /*
   * O modo fica guardado à parte do recorte: o recorte que chega pela URL
   * substitui os filtros, mas não muda como a pessoa prefere ver a lista.
   */
  const [visao, setVisao] = useStoredState<Visao>(
    `obras.visao:${organization?.id ?? '-'}:${user?.id ?? '-'}`,
    'cartoes',
    (raw) => (raw === 'cartoes' || raw === 'lista' ? raw : undefined),
  );

  useEffect(() => {
    if (fromUrl) void navigate({ to: '/obras', search: {}, replace: true });
  }, [fromUrl, navigate]);

  /** Altera o recorte, no formato que cada controle entrega. */
  function changeFilters(patch: Partial<ListState>): void {
    setListState((current) => ({ ...current, ...patch }));
  }

  const recorte = useObraQueryFilters(listState);

  const filters = useMemo(
    () => ({ ...recorte, page, perPage: 12 }) satisfies Partial<ListObrasQuery>,
    [recorte, page],
  );

  const { data, isPending, isFetching, isPlaceholderData, isError, error, refetch } =
    useObras(filters);
  const topoDaGrade = useRef<HTMLDivElement>(null);

  const activeFilters = countActiveFilters(listState);

  /*
   * Filtro novo, primeira página. O ajuste é durante a renderização, e não num
   * efeito: no efeito, o React chegava a montar a lista pedindo a página 4 de
   * um resultado que agora tem uma, e só então corrigia.
   */
  const assinaturaDoFiltro = JSON.stringify(recorte);
  const [assinaturaDoFiltroAnterior, setAssinaturaDoFiltroAnterior] = useState(assinaturaDoFiltro);
  if (assinaturaDoFiltroAnterior !== assinaturaDoFiltro) {
    setAssinaturaDoFiltroAnterior(assinaturaDoFiltro);
    changeFilters({ page: 1 });
  } else if (data && data.page === page && data.page > data.totalPages) {
    /*
     * A página guardada pode ter deixado de existir: obra excluída, recorte
     * que encolheu desde a última visita. A API responde a página pedida,
     * vazia, e a tela diria "nenhuma obra" sobre um recorte que tem obras.
     * Compara com a página do próprio resultado porque, durante a troca,
     * `data` ainda é o resultado anterior.
     */
    changeFilters({ page: data.totalPages });
  }

  /**
   * A paginação fica no pé da grade: sem subir, a página nova entraria fora
   * da vista, e quem clicou veria só a paginação mudar de número.
   */
  function irParaPagina(next: number): void {
    changeFilters({ page: next });
    const topo = topoDaGrade.current;
    if (!topo || topo.getBoundingClientRect().top >= 0) return;
    const reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    topo.scrollIntoView({ behavior: reduzido ? 'auto' : 'smooth', block: 'start' });
  }

  const vazio = (
    <Card>
      <EmptyState
        icon={activeFilters > 0 || search ? SlidersHorizontal : HardHat}
        title={
          activeFilters > 0 || search ? 'Nenhuma obra com esses filtros' : 'Nenhuma obra cadastrada'
        }
        description={
          activeFilters > 0 || search
            ? 'Remova algum filtro ou revise os termos da busca.'
            : 'Cadastre a primeira obra para começar o acompanhamento.'
        }
        action={
          activeFilters > 0 ? (
            <Button onClick={() => changeFilters(NO_FILTERS)}>Limpar filtros</Button>
          ) : may('canCreateObras') && !search ? (
            <Button
              variant="primary"
              icon={<Plus {...ICON} size={16} />}
              onClick={() => void navigate({ to: '/obras/nova' })}
            >
              Nova obra
            </Button>
          ) : null
        }
      />
    </Card>
  );

  /** O que ocupa o lugar da lista, conforme o modo e o estado da consulta. */
  function conteudo(): ReactNode {
    if (isError) {
      return (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      );
    }

    if (isPending) {
      return visao === 'lista' ? (
        <ObrasListaSkeleton />
      ) : (
        <div className="grid auto-rows-fr gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <ObraCardSkeleton key={index} />
          ))}
        </div>
      );
    }

    if (data.items.length === 0) return vazio;

    return (
      <>
        {/*
         * A grade é trocada inteira quando o conjunto muda — página, filtro,
         * ordem —, não quando a mesma lista só revalida. Enquanto o conjunto
         * novo não chega, o anterior fica esmaecido no lugar.
         */}
        <div
          ref={topoDaGrade}
          className={cn(
            'scroll-mt-20 transition-opacity duration-200',
            isPlaceholderData && 'opacity-60',
          )}
        >
          {visao === 'lista' ? (
            <ObrasLista obras={data.items} />
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={data.items.map((obra) => obra.id).join(',')}
                variants={obraGrid}
                initial="hidden"
                animate="shown"
                exit="gone"
                className="grid auto-rows-fr gap-3 sm:grid-cols-2 xl:grid-cols-3"
              >
                {data.items.map((obra, index) => (
                  <motion.div key={obra.id} variants={obraGridItem} custom={index}>
                    <ObraCard obra={obra} index={index} />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        <Pagination page={page} totalPages={data.totalPages} onChange={irParaPagina} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Obras"
        description="Todo o portfólio. Use os filtros para restringir a listagem."
        action={
          may('canCreateObras') ? (
            <Button
              variant="primary"
              icon={<Plus {...ICON} size={16} />}
              onClick={() => void navigate({ to: '/obras/nova' })}
            >
              Nova obra
            </Button>
          ) : null
        }
      />

      <ObraFilterBar
        state={listState}
        onChange={changeFilters}
        leading={
          <Segmented
            label="Modo de visualização"
            options={VISOES}
            value={visao}
            onChange={setVisao}
          />
        }
      />

      <div className="mt-3 mb-5 min-h-[22px] border-t border-line pt-3">
        {data ? (
          <SummaryStrip summary={data.summary} total={data.total} showsUpdates={can('gestor')} />
        ) : null}
      </div>

      {/* Revalidação não desmonta o conteúdo: só sinaliza com a barra fina. */}
      <div className="mb-4">
        <RefreshingBar active={isFetching && !isPending} />
      </div>

      {conteudo()}
    </>
  );
}
